import React, { useCallback, useEffect, useState } from 'react';
import { Star, MousePointerClick, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_LABELS = { buy_class: 'Booking', pricing: 'Pricing', website: 'Website', phone: 'Phone', maps: 'Directions', social: 'Social', email: 'Email', other: 'Other' };

export default function FeaturedGymClicksCard({ gymId = 'dallas-performance-systems' }) {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/featured-gym-clicks?gym_id=${gymId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
    } catch (e) { setErr(e.message); }
  }, [token, gymId]);

  useEffect(() => { load(); }, [load]);

  if (err) return null;
  if (!data) return <div className="bg-white rounded-xl border border-gray-100 p-5"><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /></div>;

  const { gym, stats, kinds, daily } = data;
  const maxDay = Math.max(1, ...daily.map((d) => d.total));

  return (
    <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-200 p-5" data-testid="featured-gym-clicks-card">
      <div className="flex items-center gap-2 mb-1">
        <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Featured Gym Spotlight</h3>
      </div>
      <p className="text-lg font-bold text-gray-900" data-testid="featured-gym-name">{gym.name}</p>
      <p className="text-xs text-gray-500 mb-4">{gym.city}{gym.state ? `, ${gym.state}` : ''} — traffic you're sending from the {gym.directory ? gym.directory.replace('-hyrox', '').toUpperCase() : 'gym'} directory</p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {[['last_7d', 'Last 7 days'], ['last_30d', 'Last 30 days'], ['all_time', 'All time']].map(([k, label]) => (
          <div key={k} className="bg-white rounded-lg border border-gray-100 p-3" data-testid={`featured-clicks-${k}`}>
            <p className="text-2xl font-bold text-amber-600 flex items-center gap-1.5">
              <MousePointerClick className="w-4 h-4" />{stats[k].booking}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400">Booking clicks · {label}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{stats[k].total} total outbound</p>
          </div>
        ))}
      </div>

      {daily.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Last 14 days</p>
          <div className="flex items-end gap-1 h-12">
            {daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col justify-end" title={`${d.day}: ${d.booking} booking / ${d.total} total`}>
                <div className="bg-amber-400 rounded-t" style={{ height: `${Math.max(8, (d.total / maxDay) * 100)}%` }} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5" data-testid="featured-clicks-kinds">
        {Object.entries(kinds).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
          <span key={k} className="px-2 py-1 bg-white border border-gray-100 rounded text-[11px] text-gray-600">
            {KIND_LABELS[k] || k}: <strong className="text-gray-900">{n}</strong>
          </span>
        ))}
        {Object.keys(kinds).length === 0 && <span className="text-xs text-gray-400">No clicks tracked yet — they'll appear as visitors hit the DPS booking links.</span>}
      </div>
    </div>
  );
}
